import {useLocation} from 'react-router';

export const integrationNames = {
    dynamics: 'dynamics',
    salesforce: 'salesforce',
    onlyDesigner: 'onlyDesigner'
};

const DESIGNER_ENTITIES = {
    HEADER: 'header',
    SEND_BUTTON: 'sendButton',
    NAVIGATION_MENU: 'navigationMenu',
    COOKIE_BANNER: 'cookieBanner',
    GO_BACK_BUTTON: 'goBackButton',
    ADD_DOCUMENT: 'addDocument',
    REMOVE_DOCUMENT: 'removeDocument',
    RECIPIENTS: 'recipients',
    SETTINGS: 'settings',
    ATTACHMENTS: 'attachments',
    DOCUMENT_VISIBILITY: 'documentVisibility',
    SAVE_BUTTON: 'saveButton',
    LAYOUTS: 'layouts',
    CUSTOM_FIELDS: 'customFields',
    FIELDS: 'fields',
    FIELD_SETTINGS: 'fieldSettings',
    ACCEPT_ONLY: 'acceptOnly',
    SIGNING_ORDER: 'signingOrder'
};

export const integrationOrigins = {
    [integrationNames.dynamics]: ['unq', 'org'],
    [integrationNames.salesforce]: ['salesforce']
};

const integrationsProfiles = {
    [integrationNames.onlyDesigner]: {
        hideHeader: true,
        hideSendButton: false,
        hideGoBackButton: true,
        hideCookieBanner: true,
        hideNavigationMenu: false
    },
    [integrationNames.salesforce]: {
        hideHeader: true,
        hideSendButton: true,
        hideGoBackButton: true,
        hideCookieBanner: true,
        hideNavigationMenu: false
    },
    [integrationNames.dynamics]: {
        hideHeader: true,
        hideSendButton: true,
        hideGoBackButton: true,
        hideCookieBanner: true,
        hideNavigationMenu: false
    }
};

function getProfileByUrlParam(param = '') {
    const profileNames = Object.keys(integrationsProfiles);
    // Find the key that matches the beginning of the param
    const matchedProfileName = profileNames.find((name) => param?.startsWith(name));

    return matchedProfileName ? integrationsProfiles[matchedProfileName] : null;
}

export default function useAccessRestrictions() {
    const {search} = useLocation();
    const searchParams = new URLSearchParams(search);
    const profile = searchParams.get('profile');
    const entitiesToHide = searchParams.get('disable')?.split(',') || [];
    const entitiesToLock = searchParams.get('readonly')?.split(',') || [];

    function getIsHidden(entity) {
        return entitiesToHide.includes(entity);
    }

    // fields has no hide behavior, so disable must not cancel its read-only state
    function getIsReadOnly(...entities) {
        return entities.some((entity) => entitiesToLock.includes(entity)
            && (entity === DESIGNER_ENTITIES.FIELDS || !getIsHidden(entity)));
    }

    return {
        hideSendButton: getIsHidden(DESIGNER_ENTITIES.SEND_BUTTON),
        hideHeader: getIsHidden(DESIGNER_ENTITIES.HEADER),
        hideGoBackButton: getIsHidden(DESIGNER_ENTITIES.GO_BACK_BUTTON),
        hideNavigationMenu: getIsHidden(DESIGNER_ENTITIES.NAVIGATION_MENU),
        hideCookieBanner: getIsHidden(DESIGNER_ENTITIES.COOKIE_BANNER),
        hideAddDocument: getIsHidden(DESIGNER_ENTITIES.ADD_DOCUMENT),
        hideRemoveDocument: getIsHidden(DESIGNER_ENTITIES.REMOVE_DOCUMENT),
        hideRecipients: getIsHidden(DESIGNER_ENTITIES.RECIPIENTS),
        hideSettings: getIsHidden(DESIGNER_ENTITIES.SETTINGS),
        hideAttachments: getIsHidden(DESIGNER_ENTITIES.ATTACHMENTS),
        hideDocumentVisibility: getIsHidden(DESIGNER_ENTITIES.DOCUMENT_VISIBILITY),
        hideSaveButton: getIsHidden(DESIGNER_ENTITIES.SAVE_BUTTON),
        hideLayouts: getIsHidden(DESIGNER_ENTITIES.LAYOUTS),
        hideCustomFields: getIsHidden(DESIGNER_ENTITIES.CUSTOM_FIELDS),
        hideFieldSettings: getIsHidden(DESIGNER_ENTITIES.FIELD_SETTINGS),
        hideAcceptOnly: getIsHidden(DESIGNER_ENTITIES.ACCEPT_ONLY),
        hideSigningOrder: getIsHidden(DESIGNER_ENTITIES.SIGNING_ORDER),
        isLayoutsReadOnly: getIsReadOnly(DESIGNER_ENTITIES.LAYOUTS),
        isCustomFieldsReadOnly: getIsReadOnly(DESIGNER_ENTITIES.CUSTOM_FIELDS, DESIGNER_ENTITIES.FIELDS),
        isFieldsReadOnly: getIsReadOnly(DESIGNER_ENTITIES.FIELDS),
        isFieldSettingsReadOnly: getIsReadOnly(DESIGNER_ENTITIES.FIELD_SETTINGS, DESIGNER_ENTITIES.FIELDS),
        isAcceptOnlyReadOnly: getIsReadOnly(DESIGNER_ENTITIES.ACCEPT_ONLY, DESIGNER_ENTITIES.FIELDS),
        isSigningOrderReadOnly: getIsReadOnly(DESIGNER_ENTITIES.SIGNING_ORDER),
        // Profile should have higher priority of the configuration over the url parameters
        ...getProfileByUrlParam(profile)
    };
}

export function getProfileName() {
    const profileName = new URLSearchParams(window.location.search).get('profile');
    return profileName;
}
